import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons';

import VenueMapCanvas, { MapPin } from './VenueMap';
import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import {
  directionsUrls,
  formatAddress,
  formatDistance,
  LatLng,
  prefersAppleMaps,
  PublicVenue,
  sortVenues,
} from '../utils/VenueMap';

import './JukeboxView.scss';
import './VenuesView.scss';
import AppHeader from './AppHeader';
import FooterContent from './FooterContent';

type Locate = 'idle' | 'asking' | 'found' | 'denied' | 'unavailable';

/**
 * "Venues near you": every venue whose owner put it on the Jookable map, on an
 * OpenStreetMap map and in a list. Open to everyone, signed in or not.
 *
 * Near me asks the browser where the visitor is, centres the map there and lists the
 * venues nearest first. Without that it shows them all, by name.
 *
 * It shows where a venue is and how to get there, and nothing that would let someone
 * queue songs from afar: no player codes, no jukebox links.
 */
const VenuesView = () => {
  const [venues, setVenues] = useState<PublicVenue[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [you, setYou] = useState<LatLng | null>(null);
  const [locate, setLocate] = useState<Locate>('idle');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const apple = prefersAppleMaps();

  useEffect(() => {
    api
      .venues('', 'GET', null, null)
      .then(response => setVenues(Array.isArray(response.venues) ? response.venues : []))
      .catch(error => {
        setVenues([]);
        setLoadError(error.message);
      });
  }, []);

  const nearMe = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocate('unavailable');
      return;
    }
    setLocate('asking');
    navigator.geolocation.getCurrentPosition(
      position => {
        setYou({ lat: position.coords.latitude, lng: position.coords.longitude });
        setSelectedId(null);
        setLocate('found');
        trackEvent('venues_near_me', { result: 'found' });
      },
      error => {
        setLocate(error && error.code === 1 ? 'denied' : 'unavailable');
        trackEvent('venues_near_me', { result: error && error.code === 1 ? 'denied' : 'unavailable' });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60 * 1000 },
    );
  };

  const sorted = useMemo(() => sortVenues(venues || [], you), [venues, you]);
  const pins: MapPin[] = useMemo(() => sorted.map(v => ({ id: v.id, lat: v.lat, lng: v.lng, label: v.name })), [sorted]);
  const selected = sorted.find(v => v.id === selectedId) || null;
  const focus = selected ? { lat: selected.lat, lng: selected.lng } : you;

  const popup = useCallback(
    (pin: MapPin) => {
      const venue = (venues || []).find(v => v.id === pin.id);
      const box = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'venue-popup-name';
      name.textContent = pin.label;
      box.appendChild(name);
      if (venue) {
        const address = formatAddress(venue);
        if (address) {
          const line = document.createElement('div');
          line.className = 'venue-popup-address';
          line.textContent = address;
          box.appendChild(line);
        }
        const link = document.createElement('a');
        const urls = directionsUrls(venue);
        link.className = 'venue-popup-link';
        link.href = apple ? urls.apple : urls.google;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Directions';
        box.appendChild(link);
      }
      return box;
    },
    [venues, apple],
  );

  const locateNote: { [key in Locate]?: string } = {
    asking: 'Finding you…',
    denied: 'Location is off for this site, so here is every venue.',
    unavailable: 'We could not find your location, so here is every venue.',
  };

  return (
    <div className="jukebox venues">
      <AppHeader />

      <div className="jukebox-body">
        <div className="jukebox-panel venues-panel">
          <div className="venues-heading">
            <div>
              <h1 className="venues-title">Venues near you</h1>
              <p className="venues-intro">Bars and restaurants with a Jookable jukebox.</p>
            </div>
            <button type="button" className="venues-near-me" onClick={nearMe} disabled={locate === 'asking'}>
              <FontAwesomeIcon icon={faLocationArrow} /> Near me
            </button>
          </div>

          {!!locateNote[locate] && <div className="venues-note">{locateNote[locate]}</div>}

          <VenueMapCanvas
            pins={pins}
            you={you}
            focus={focus}
            focusZoom={selected ? 15 : 11}
            selectedId={selectedId}
            onSelect={setSelectedId}
            popup={popup}
            ariaLabel="Map of Jookable venues"
          />

          {venues === null && <p className="jukebox-empty">Loading venues…</p>}
          {!!loadError && <p className="jukebox-error">{loadError}</p>}
          {venues !== null && !loadError && venues.length === 0 && (
            <p className="jukebox-empty">No venues on the map yet. Check back soon.</p>
          )}

          {sorted.length > 0 && (
            <ul className="venues-list" aria-label={you ? 'Venues, nearest first' : 'Venues'}>
              {sorted.map(venue => {
                const urls = directionsUrls(venue);
                const address = formatAddress(venue);
                return (
                  <li key={venue.id} className={venue.id === selectedId ? 'venue-card is-selected' : 'venue-card'}>
                    <button
                      type="button"
                      className="venue-card-main"
                      onClick={() => {
                        setSelectedId(venue.id);
                        trackEvent('venue_selected', { venue_id: venue.id });
                      }}
                      aria-label={`Show ${venue.name} on the map`}
                    >
                      {venue.logoUrl ? (
                        <img className="venue-card-logo" src={venue.logoUrl} alt="" />
                      ) : (
                        <span className="venue-card-logo is-empty" aria-hidden="true">
                          {venue.name.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <span className="venue-card-text">
                        <span className="venue-card-name">{venue.name}</span>
                        {!!address && <span className="venue-card-address">{address}</span>}
                      </span>
                      {venue.km != null && <span className="venue-card-distance">{formatDistance(venue.km)}</span>}
                    </button>
                    <div className="venue-card-links">
                      <a
                        className="venue-card-directions"
                        href={apple ? urls.apple : urls.google}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Directions
                      </a>
                      <a className="venue-card-other" href={apple ? urls.google : urls.apple} target="_blank" rel="noopener noreferrer">
                        {apple ? 'Google Maps' : 'Apple Maps'}
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="venues-owner-hint">
            Run a venue with Jookable? Turn on the map under Settings in your jukebox&apos;s control panel.
          </p>
        </div>
      </div>

      <div className="footer">
        <FooterContent />
      </div>
    </div>
  );
};

export default VenuesView;
